/**
 * TriggerRmsDocumentGeneration
 * ----------------------------
 * Fires the `rmsGenerateTriageLetter` Cloud Function in a fire-and-forget pattern
 * after a triage decision is submitted (outcome 1: accepted, or outcome 3: dual-discharge).
 *
 * Follows the same retry-with-backoff pattern used by the ADHD assessment's
 * TriggerGenerateDocumentSection utility, with a short initial delay to allow
 * Firestore writes from the triage submission to propagate before the Cloud
 * Function reads them.
 *
 * Usage:
 *   import { TriggerRmsDocumentGeneration } from './TriggerRmsDocumentGeneration';
 *   TriggerRmsDocumentGeneration({ orgId, patientRecordId });
 */

import { auth, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const TriggerRmsDocumentGeneration = ({ orgId, patientRecordId, pathway }) => {
  const rmsGenerateTriageLetter = httpsCallable(functions, 'rmsGenerateTriageLetter', {
    timeout: 360000,
  });

  const user = auth.currentUser;
  if (!user || !user.uid) {
    console.error('TriggerRmsDocumentGeneration: User not authenticated');
    return;
  }

  if (!orgId || !patientRecordId) {
    console.error('TriggerRmsDocumentGeneration: Missing orgId or patientRecordId');
    return;
  }

  // No fixed wait (TDI-396): the decision is committed before the submit
  // callable returns, so the generation reads a settled record. The server
  // now starts the drafts itself from rmsSubmitTriageDecision; this call is
  // the Retry path and the fallback when the queue could not take the task.
  Promise.resolve().then(() => {
    console.log('Triggering RMS triage letter generation for:', patientRecordId);

    const MAX_RETRIES = 3;
    const BASE_DELAY = 2000;
    const TIMEOUT_MS = 5 * 60 * 1000;

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Triage letter generation timed out after 5 minutes')), TIMEOUT_MS);
    });

    const attemptGeneration = async (attempt = 1) => {
      try {
        // Back off only before a retry, never before the first call.
        if (attempt > 1) await delay(BASE_DELAY * attempt);

        const result = await Promise.race([
          rmsGenerateTriageLetter({ orgId, patientRecordId, ...(pathway ? { pathway } : {}) }),
          timeoutPromise,
        ]);

        console.log('RMS triage letter generation response:', result.data);
        return result;
      } catch (error) {
        console.error(`RMS generation attempt ${attempt} failed:`, error);

        if (error.message?.includes('timed out')) {
          throw new Error('The letter generation took too long. Please try again.');
        }

        if (error.code === 'functions/resource-exhausted' && attempt < MAX_RETRIES) {
          console.log(`Retrying in ${(BASE_DELAY * (attempt + 1)) / 1000} seconds...`);
          return attemptGeneration(attempt + 1);
        }

        throw error;
      }
    };

    attemptGeneration().catch((error) => {
      console.error('All RMS letter generation retry attempts failed:', error);
    });
  });
};
